import React from 'react';
import { EVENTS, trackEvent } from '../../analytics/trackEvent';
import { useI18n } from '../../i18n/I18nContext';
import { getVolumeLettersPath } from '../../i18n/locale';
import { LegoInput } from './LegoInput';
import { LegoPreview } from './LegoPreview';
import { BaseplateSize, LegoSize } from '../../types/LegoTypes';
import './LegoBuilder.css';

interface LegoBuilderProps {
  name: string;
  onNameChange: (name: string) => void;
}

export const LegoBuilder: React.FC<LegoBuilderProps> = ({ name, onNameChange }) => {
  const [size, setSize] = React.useState<LegoSize>('M');
  const [baseplate, setBaseplate] = React.useState<BaseplateSize>('16x32');
  const { locale, t } = useI18n();

  return (
    <div className='lego-builder'>
      <div className='lego-controls'>
        <LegoInput value={name} onChange={onNameChange} />
        <div className='size-selectors-row'>
          <div className='size-selector'>
            <label htmlFor='size-select'>{t.sizeLabel}</label>
            <div className='size-buttons'>
              {(['S', 'M', 'L'] as LegoSize[]).map((s) => (
                <button
                  key={s}
                  type='button'
                  className={`size-button ${size === s ? 'active' : ''}`}
                  onClick={() => {
                    trackEvent(EVENTS.LETTER_SIZE, { size: s });
                    setSize(s);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className='size-selector size-selector--end'>
            <label>{t.baseplateLabel}</label>
            <div className='size-buttons'>
              {(['16x32', '32x32'] as BaseplateSize[]).map((bp) => (
                <button
                  key={bp}
                  type='button'
                  className={`size-button ${baseplate === bp ? 'active' : ''}`}
                  onClick={() => {
                    trackEvent(EVENTS.BASEPLATE, { size: bp });
                    setBaseplate(bp);
                  }}
                >
                  {bp}
                </button>
              ))}
            </div>
          </div>
        </div>
        <a
          className='design-3d-link'
          href={getVolumeLettersPath(locale)}
          data-umami-event={EVENTS.VOLUME_LETTERS}
          data-umami-event-source='generator'
        >
          <span className='design-3d-link__icon' aria-hidden='true'>
            ▲
          </span>
          <span className='design-3d-link__text'>
            {t.design3dButton}
            <span className='design-3d-link__badge'>{t.design3dBadge}</span>
          </span>
        </a>
      </div>
      <LegoPreview name={name} size={size} baseplate={baseplate} />
    </div>
  );
};
